/**
 * Modale "Définissez votre mot de passe maintenant"
 *
 * Affichée automatiquement après une connexion via magic link
 * (URL contient `?must_set_password=1`). Utilise le `oscop_magic_token`
 * stocké en sessionStorage par /auth/magic pour appeler le backend.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Lock, ShieldCheck, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { PasswordInput } from './ui/password-input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SetPasswordModal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const flag = searchParams.get('must_set_password');
  const magicToken = typeof window !== 'undefined' ? sessionStorage.getItem('oscop_magic_token') : null;

  const [open, setOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (flag === '1' && magicToken) {
      setOpen(true);
    }
  }, [flag, magicToken]);

  const handleClose = () => {
    setOpen(false);
    // Retire le flag de l'URL pour éviter qu'on rouvre la modale au refresh
    const params = new URLSearchParams(searchParams);
    params.delete('must_set_password');
    setSearchParams(params, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/magic-link-set-password`, {
        token: magicToken,
        new_password: newPassword,
      });
      setSuccess(true);
      sessionStorage.removeItem('oscop_magic_token');
      setTimeout(() => {
        handleClose();
        // Refresh sur la même route sans le flag
        navigate(location.pathname, { replace: true });
      }, 1500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Impossible de définir le mot de passe. Réessayez ou demandez un nouveau lien.');
    } finally {
      setLoading(false);
    }
  };

  if (!magicToken || flag !== '1') return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden"
        data-testid="set-password-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <DialogHeader>
                <DialogTitle className="text-white text-lg leading-tight">
                  Définissez votre mot de passe maintenant
                </DialogTitle>
                <DialogDescription className="text-slate-300 text-xs mt-1">
                  Pour finaliser votre connexion sécurisée
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {success ? (
            <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-emerald-800 text-sm">Mot de passe défini</p>
                <p className="text-emerald-700 text-xs mt-0.5">Vous pouvez maintenant utiliser votre espace.</p>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span data-testid="set-password-error">{error}</span>
                </div>
              )}

              <div>
                <Label htmlFor="set-pwd-new" className="text-sm font-semibold text-slate-700">
                  Nouveau mot de passe
                </Label>
                <div className="relative mt-1.5">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
                  <PasswordInput
                    id="set-pwd-new"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 8 caractères"
                    className="pl-10 h-11"
                    autoFocus
                    required
                    autoComplete="new-password"
                    data-testid="set-password-new-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="set-pwd-confirm" className="text-sm font-semibold text-slate-700">
                  Confirmer le mot de passe
                </Label>
                <div className="relative mt-1.5">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
                  <PasswordInput
                    id="set-pwd-confirm"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Retapez votre mot de passe"
                    className="pl-10 h-11"
                    required
                    autoComplete="new-password"
                    data-testid="set-password-confirm-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-[#0B3C5D] to-[#0a1e3f] hover:from-[#0a3252] hover:to-[#091a36] text-white font-semibold"
                data-testid="set-password-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Définir mon mot de passe
                  </>
                )}
              </Button>
              <p className="text-[11px] text-slate-500 text-center mt-1">
                Ce lien magique expirera dans quelques minutes. Définissez votre mot de passe maintenant pour sécuriser votre compte.
              </p>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SetPasswordModal;
